import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminToast, { useAdminToast } from '../AdminToast';
import HomepageHeroManager from '../admin/HomepageHeroManager';
import AdminCalendarManager from '../admin/AdminCalendarManager';
import AdminInquiriesManager from '../admin/AdminInquiriesManager';
import logoImage from '../../img/napvirag_logo1.png';
import { getDocumentUrl } from '../../utils/documentUrl';
import './styles/AdminPanel.css';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const AdminPanel = () => {
  const { toasts, showToast, dismissToast } = useAdminToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'announcements');
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loginError, setLoginError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const getAllowedTabs = (user) => {
    if (!user) return [];
    if (user.role === 'admin') {
      return ['announcements', 'menu', 'calendar', 'documents', 'homepage', 'inquiries'];
    }
    const p = user.permissions || {};
    const tabs = [];
    if (p.canManageAnnouncements) tabs.push('announcements');
    if (p.canManageMenu) tabs.push('menu');
    if (p.canManageEvents) tabs.push('calendar');
    if (p.canManageDocuments) tabs.push('documents');
    if (p.canManageGallery) tabs.push('homepage');
    if (p.canManageInquiries) tabs.push('inquiries');
    return tabs;
  };

  const pickDefaultTab = (user) => {
    const allowed = getAllowedTabs(user);
    // Prefer the most common workflows
    if (allowed.includes('announcements')) return 'announcements';
    if (allowed.includes('menu')) return 'menu';
    if (allowed.includes('calendar')) return 'calendar';
    if (allowed.includes('documents')) return 'documents';
    if (allowed.includes('homepage')) return 'homepage';
    if (allowed.includes('inquiries')) return 'inquiries';
    return 'announcements';
  };

  const resolveActiveTab = useCallback(
    (user, preferred) => {
      const allowed = getAllowedTabs(user);
      const tab = preferred || searchParams.get('tab');
      if (tab && allowed.includes(tab)) return tab;
      return pickDefaultTab(user);
    },
    [searchParams]
  );

  const selectTab = useCallback(
    (tabId) => {
      setActiveTab(tabId);
      setSearchParams({ tab: tabId }, { replace: true });
    },
    [setSearchParams]
  );

  // API Helper function
  const apiCall = async (endpoint, method = 'GET', body = null) => {
    const token = localStorage.getItem('adminToken');
    const config = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    };

    if (body) {
      config.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'API request failed');
      }

      return data;
    } catch (error) {
      console.error(`API call failed for ${endpoint}:`, error);
      throw error;
    }
  };

  // Check if user is already logged in with valid JWT token
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      verifyToken(token);
    }
  }, []);

  // Verify JWT token with backend
  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentUser(data.user);
        setIsAuthenticated(true);
        const tab = resolveActiveTab(data.user, searchParams.get('tab'));
        setActiveTab(tab);
        setSearchParams({ tab }, { replace: true });
      } else {
        // Token is invalid, remove it
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      setIsAuthenticated(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: loginData.username,
          password: loginData.password,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Store JWT token and user info
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminUser', data.user.username);
        setCurrentUser(data.user);
        setIsAuthenticated(true);
        const tab = resolveActiveTab(data.user, searchParams.get('tab'));
        setActiveTab(tab);
        setSearchParams({ tab }, { replace: true });
        setLoginData({ username: '', password: '' });
        setLoginError('');
      } else {
        // Show error message from server
        setLoginError(data.error || 'Hibás felhasználónév vagy jelszó!');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Kapcsolódási hiba. Ellenőrizze a szerver állapotát.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setCurrentUser(null);
    setIsAuthenticated(false);
    setActiveTab('announcements');
    setSearchParams({}, { replace: true });
    setLoginError('');
  };

  useEffect(() => {
    if (!isAuthenticated || !currentUser) return;
    const allowed = getAllowedTabs(currentUser);
    if (!allowed.includes(activeTab)) {
      const fallback = pickDefaultTab(currentUser);
      setActiveTab(fallback);
      setSearchParams({ tab: fallback }, { replace: true });
    }
  }, [isAuthenticated, currentUser, activeTab, setSearchParams]);

  if (!isAuthenticated) {
    return (
      <div className="admin-page admin-page--login">
        <div className="admin-login-container">
          <div className="login-card">
            <div className="login-header">
              <img src={logoImage} alt="Napvirág" className="login-logo" />
              <h1 className="login-title">Napvirág Admin</h1>
              <p className="login-subtitle">Napvirág Idősek Otthona</p>
              <div className="login-divider" aria-hidden="true">
                <span className="login-divider__line" />
                <span className="login-divider__leaf">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3A4.73 4.73 0 0 0 8 20c4 0 8.3-3.4 9-12 .1-.85.1-1.7 0-2.5C16.3 4.6 12 1 8 1a4.73 4.73 0 0 0-1.29.3l-1 2.3L3.82 2.66C5.9 7.83 8 14 17 16c.85.15 1.7.15 2.5 0z" />
                  </svg>
                </span>
                <span className="login-divider__line" />
              </div>
              <p className="login-hint">Személyzet számára fenntartott terület</p>
            </div>

            <form onSubmit={handleLogin} className="login-form">
              <div className="form-group">
                <label htmlFor="username">Felhasználónév</label>
                <div className="login-input-wrap">
                  <span className="login-input-icon" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </span>
                <input
                  type="text"
                  id="username"
                  value={loginData.username}
                    onChange={(e) => setLoginData((prev) => ({ ...prev, username: e.target.value }))}
                  required
                    placeholder="felhasználónév"
                    autoComplete="username"
                />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password">Jelszó</label>
                <div className="login-input-wrap login-input-wrap--password">
                  <span className="login-input-icon" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </span>
                <input
                    type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={loginData.password}
                    onChange={(e) => setLoginData((prev) => ({ ...prev, password: e.target.value }))}
                  required
                    placeholder="jelszó"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? 'Jelszó elrejtése' : 'Jelszó megjelenítése'}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {loginError && (
                <div className="error-message" role="alert">
                  {loginError}
                </div>
              )}

              <button type="submit" className="login-button" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner" />
                    Bejelentkezés...
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    Bejelentkezés
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-container">
        {/* Admin Header */}
        <div className="admin-header">
          <div className="admin-title">
            <img src={logoImage} alt="Napvirág Idősek Otthona" className="admin-header-logo" />
          </div>
          <div className="admin-user">
            <span>👤 {currentUser ? (currentUser.nickname || currentUser.username) : 'Felhasználó'}</span>
            {currentUser && (
              <span className="user-role">({currentUser.role})</span>
            )}
            <button onClick={handleLogout} className="logout-button">
              🚪 Kijelentkezés
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="admin-nav">
          {getAllowedTabs(currentUser).includes('announcements') && (
          <button
            className={`nav-tab ${activeTab === 'announcements' ? 'active' : ''}`}
              onClick={() => selectTab('announcements')}
          >
            📢 Bejelentések
          </button>
          )}
          {getAllowedTabs(currentUser).includes('menu') && (
          <button
            className={`nav-tab ${activeTab === 'menu' ? 'active' : ''}`}
              onClick={() => selectTab('menu')}
          >
            🍽️ Heti Étlap
          </button>
          )}
          {getAllowedTabs(currentUser).includes('calendar') && (
          <button
            className={`nav-tab ${activeTab === 'calendar' ? 'active' : ''}`}
              onClick={() => selectTab('calendar')}
          >
            📅 Naptár
          </button>
          )}
          {getAllowedTabs(currentUser).includes('documents') && (
          <button
              className={`nav-tab ${activeTab === 'documents' ? 'active' : ''}`}
              onClick={() => selectTab('documents')}
          >
              📄 Dokumentumok
          </button>
          )}
          {getAllowedTabs(currentUser).includes('homepage') && (
          <button
              className={`nav-tab ${activeTab === 'homepage' ? 'active' : ''}`}
              onClick={() => selectTab('homepage')}
          >
              🏠 Főoldal
          </button>
          )}
          {getAllowedTabs(currentUser).includes('inquiries') && (
            <button
              className={`nav-tab ${activeTab === 'inquiries' ? 'active' : ''}`}
              onClick={() => selectTab('inquiries')}
            >
              📬 Érdeklődések
            </button>
          )}
        </div>

        {/* Content Area */}
        <div className="admin-content">
          {activeTab === 'announcements' && getAllowedTabs(currentUser).includes('announcements') && (
            <AnnouncementsManager apiCall={apiCall} currentUser={currentUser} showToast={showToast} />
          )}
          {activeTab === 'menu' && getAllowedTabs(currentUser).includes('menu') && <MenuManager showToast={showToast} />}
          {activeTab === 'calendar' && getAllowedTabs(currentUser).includes('calendar') && (
            <AdminCalendarManager apiCall={apiCall} showToast={showToast} currentUser={currentUser} />
          )}
          {activeTab === 'documents' && getAllowedTabs(currentUser).includes('documents') && (
            <DocumentsManager apiCall={apiCall} showToast={showToast} />
          )}
          {activeTab === 'homepage' && getAllowedTabs(currentUser).includes('homepage') && (
            <HomepageHeroManager apiCall={apiCall} showToast={showToast} />
          )}
          {activeTab === 'inquiries' && getAllowedTabs(currentUser).includes('inquiries') && (
            <AdminInquiriesManager apiCall={apiCall} showToast={showToast} />
          )}
        </div>
      </div>
      <AdminToast toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
};

// Announcements Manager Component
const AnnouncementsManager = ({ apiCall, currentUser, showToast }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [aktualitasokPageEnabled, setAktualitasokPageEnabled] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'info',
    pinned: false
  });
  const editorRef = React.useRef(null);

  useEffect(() => {
    loadAnnouncements();
    loadPageSettings();
  }, []);

  const loadPageSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/settings/public`);
      if (response.ok) {
        const data = await response.json();
        setAktualitasokPageEnabled(data.aktualitasokPageEnabled !== false);
      }
    } catch (error) {
      console.error('Failed to load page settings:', error);
    }
  };

  const handleToggleAktualitasokPage = async () => {
    const nextValue = !aktualitasokPageEnabled;
    setSettingsSaving(true);
    try {
      const data = await apiCall('/settings/aktualitasok-page', 'PUT', { enabled: nextValue });
      setAktualitasokPageEnabled(data.aktualitasokPageEnabled !== false);
    } catch (error) {
      console.error('Failed to update page settings:', error);
      showToast('Hiba történt a beállítás mentése során: ' + (error.message || 'Ismeretlen hiba'), 'error');
    } finally {
      setSettingsSaving(false);
    }
  };

  // Update editor content when editing an announcement (only when starting to edit)
  useEffect(() => {
    if (editorRef.current && showEditor && editingAnnouncement) {
      const editor = editorRef.current;
      editor.innerHTML = formData.content || '';
      editor.focus();
    } else if (editorRef.current && showEditor && !editingAnnouncement) {
      // Clear editor when creating new announcement
      const editor = editorRef.current;
      editor.innerHTML = '';
      editor.focus();
    }
  }, [showEditor, editingAnnouncement]);

  const loadAnnouncements = async () => {
    setIsLoading(true);
    try {
      const response = await apiCall('/announcements/admin/list');
      setAnnouncements(response || []);
    } catch (error) {
      console.error('Failed to load announcements:', error);
      // Keep empty array on error
      setAnnouncements([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const announcementData = {
        title: formData.title,
        content: cleanHtmlForStorage(formData.content),
        type: formData.type,
        pinned: formData.pinned
      };

      if (editingAnnouncement) {
        // Update existing announcement
        await apiCall(`/announcements/${editingAnnouncement._id}`, 'PUT', announcementData);
      } else {
        // Create new announcement
        await apiCall('/announcements', 'POST', announcementData);
      }

      // Reload announcements to get updated data
      await loadAnnouncements();

      // Reset form
      setFormData({ title: '', content: '', type: 'info', pinned: false });
      setShowEditor(false);
      setEditingAnnouncement(null);
      
      // Show success message
      showToast(editingAnnouncement ? 'Bejelentés sikeresen frissítve!' : 'Bejelentés sikeresen létrehozva!');
    } catch (error) {
      console.error('Failed to save announcement:', error);
      showToast('Hiba történt a bejelentés mentése során: ' + (error.message || 'Ismeretlen hiba'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      type: announcement.type,
      pinned: announcement.pinned
    });
    setShowEditor(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Biztosan törölni szeretné ezt a bejelentést?')) {
      setIsLoading(true);
      try {
        await apiCall(`/announcements/${id}`, 'DELETE');
        // Reload announcements to get updated data
        await loadAnnouncements();
        showToast('Bejelentés sikeresen törölve!');
      } catch (error) {
        console.error('Failed to delete announcement:', error);
        showToast('Hiba történt a bejelentés törlése során: ' + (error.message || 'Ismeretlen hiba'), 'error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'important': return '📢';
      case 'event': return '🎉';
      case 'info': return 'ℹ️';
      default: return '📝';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'important': return 'Fontos közlemény';
      case 'event': return 'Esemény';
      case 'info': return 'Információ';
      default: return 'Bejelentés';
    }
  };

  // Rich text editor functions
  const applyFormatting = (command) => {
    const editor = editorRef.current;
    if (!editor) return;
    
    editor.focus();
    
    try {
      if (command === 'insertLineBreak') {
        document.execCommand('insertHTML', false, '<br>');
      } else if (command === 'insertParagraph') {
        document.execCommand('insertHTML', false, '<br><br>');
      } else {
        document.execCommand(command, false, null);
      }
    } catch (error) {
      console.error('Formatting command failed:', error);
    }
    
    // Update content after formatting
    const newContent = editor.innerHTML;
    setFormData(prev => ({ ...prev, content: newContent }));
  };

  const handleEditorInput = (e) => {
    const content = e.target.innerHTML;
    // Use a timeout to debounce state updates and prevent cursor jumping
    setTimeout(() => {
      setFormData(prev => ({ ...prev, content }));
    }, 0);
  };

  const handleKeyDown = (e) => {
    // Handle Enter key for better line breaks
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      document.execCommand('insertHTML', false, '<br><br>');
    }
  };

  const getTextContent = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    return div.textContent || div.innerText || '';
  };

  const cleanHtmlForStorage = (html) => {
    // Clean up the HTML before storing
    return html
      .replace(/<div>/g, '<p>')
      .replace(/<\/div>/g, '</p>')
      .replace(/<br\s*\/?>/g, '<br/>');
  };

  const truncateHtml = (html, maxLength) => {
    // Get plain text content and truncate it
    const textContent = getTextContent(html);
    if (textContent.length <= maxLength) {
      // If short enough, return the full HTML
      return html;
    }
    
    // Just get the first characters as plain text and add ellipsis
    const truncatedText = textContent.substring(0, maxLength);
    return truncatedText + '...';
  };

  const formatAnnouncementDate = (dateStr) => {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}. ${m}. ${day}.`;
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'draft': return 'Piszkozat';
      case 'archived': return 'Archivált';
      default: return 'Közzétéve';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'draft': return 'status-draft';
      case 'archived': return 'status-archived';
      default: return 'status-published';
    }
  };

  const stats = {
    published: announcements.filter((a) => (a.status || 'published') === 'published').length,
    draft: announcements.filter((a) => a.status === 'draft').length,
    scheduled: 0,
    archived: announcements.filter((a) => a.status === 'archived').length
  };

  const filteredAnnouncements = announcements.filter((announcement) => {
    const status = announcement.status || 'published';
    if (statusFilter !== 'all' && status !== statusFilter) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.trim().toLowerCase();
    const haystack = [
      announcement.title,
      getTextContent(announcement.content),
      announcement.author,
      getTypeLabel(announcement.type)
    ].join(' ').toLowerCase();
    return haystack.includes(q);
  });

  const publishedVisibleCount = filteredAnnouncements.filter(
    (a) => (a.status || 'published') === 'published'
  ).length;

  const openNewEditor = () => {
            setShowEditor(true);
            setEditingAnnouncement(null);
            setFormData({ title: '', content: '', type: 'info', pinned: false });
  };

  return (
    <div className="announcements-manager">
      <div className="ann-page-header">
        <div className="ann-page-title">
          <h2>Bejelentések</h2>
          <p>Tartalomkezelő · Aktualitások és közlemények</p>
        </div>
        <button type="button" className="ann-btn-new" onClick={openNewEditor} disabled={showEditor}>
          <span className="ann-btn-new-icon" aria-hidden>+</span>
          Új Bejelentés
        </button>
      </div>

      <div className="ann-visibility-card">
        <div className="ann-visibility-icon" aria-hidden>🌐</div>
        <div className="ann-visibility-text">
          <strong>Aktualitások oldal megjelenítése a weboldalon</strong>
          <p>
            {aktualitasokPageEnabled
              ? 'Az „Aktualitások” link látható a menüben, és az oldal elérhető.'
              : 'Az „Aktualitások” link rejtve van, és az oldal nem érhető el a látogatók számára.'}
          </p>
        </div>
        <div className="ann-visibility-actions">
          <button
            type="button"
            className={`ann-switch ${aktualitasokPageEnabled ? 'is-on' : ''}`}
            role="switch"
            aria-checked={aktualitasokPageEnabled}
            onClick={handleToggleAktualitasokPage}
            disabled={settingsSaving}
          >
            <span className="ann-switch-track"><span className="ann-switch-thumb" /></span>
            <span className="ann-switch-label">{aktualitasokPageEnabled ? 'Bekapcsolva' : 'Kikapcsolva'}</span>
        </button>
          <a href="/informaciok/bejelentesek" target="_blank" rel="noopener noreferrer" className="ann-open-page-btn">
            Megnyitás ↗
          </a>
        </div>
      </div>

      <div className="ann-stats-row">
        <div className="ann-stat-card ann-stat-published">
          <div className="ann-stat-icon">📄</div>
          <div className="ann-stat-body">
            <span className="ann-stat-value">{stats.published}</span>
            <span className="ann-stat-label">Közzétett bejelentés</span>
            <span className="ann-stat-hint ann-stat-hint-green">Nyilvánosan látható</span>
          </div>
        </div>
        <div className="ann-stat-card ann-stat-draft">
          <div className="ann-stat-icon">🕐</div>
          <div className="ann-stat-body">
            <span className="ann-stat-value">{stats.draft}</span>
            <span className="ann-stat-label">Piszkozat</span>
            <span className="ann-stat-hint ann-stat-hint-orange">Közzétételre vár</span>
          </div>
        </div>
        <div className="ann-stat-card ann-stat-scheduled">
          <div className="ann-stat-icon">📅</div>
          <div className="ann-stat-body">
            <span className="ann-stat-value">{stats.scheduled}</span>
            <span className="ann-stat-label">Időzített</span>
            <span className="ann-stat-hint ann-stat-hint-blue">Később jelenik meg</span>
          </div>
        </div>
        <div className="ann-stat-card ann-stat-archived">
          <div className="ann-stat-icon">🗑️</div>
          <div className="ann-stat-body">
            <span className="ann-stat-value">{stats.archived}</span>
            <span className="ann-stat-label">Archivált</span>
            <span className="ann-stat-hint ann-stat-hint-muted">Nem látható</span>
          </div>
        </div>
      </div>

      {showEditor && (
        <div className="announcement-editor">
          <div className="editor-header">
            <h3>{editingAnnouncement ? 'Bejelentés Szerkesztése' : 'Új Bejelentés'}</h3>
            <button 
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setShowEditor(false);
                setEditingAnnouncement(null);
                setFormData({ title: '', content: '', type: 'info', pinned: false });
              }}
            >
              ✕ Bezárás
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Cím:</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                required
                placeholder="Bejelentés címe..."
              />
            </div>

            <div className="form-group">
              <label>Típus:</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              >
                <option value="info">ℹ️ Információ</option>
                <option value="important">📢 Fontos közlemény</option>
                <option value="event">🎉 Esemény</option>
              </select>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.pinned}
                  onChange={(e) => setFormData(prev => ({ ...prev, pinned: e.target.checked }))}
                />
                📌 Kiemelt bejelentés
              </label>
            </div>

            <div className="form-group">
              <label>Tartalom:</label>
              <div className="rich-text-editor">
                <div className="editor-toolbar">
                  <button 
                    type="button" 
                    className="toolbar-btn"
                    onClick={() => applyFormatting('bold')}
                    title="Félkövér"
                  >
                    <strong>B</strong>
                  </button>
                  <button 
                    type="button" 
                    className="toolbar-btn"
                    onClick={() => applyFormatting('italic')}
                    title="Dőlt"
                  >
                    <em>I</em>
                  </button>
                  <button 
                    type="button" 
                    className="toolbar-btn"
                    onClick={() => applyFormatting('underline')}
                    title="Aláhúzott"
                  >
                    <u>U</u>
                  </button>
                  <div className="toolbar-divider"></div>
                  <button 
                    type="button" 
                    className="toolbar-btn"
                    onClick={() => applyFormatting('insertUnorderedList')}
                    title="Felsorolás"
                  >
                    •
                  </button>
                  <button 
                    type="button" 
                    className="toolbar-btn"
                    onClick={() => applyFormatting('insertOrderedList')}
                    title="Számozott lista"
                  >
                    1.
                  </button>
                </div>
                <div
                  ref={editorRef}
                  contentEditable
                  className="rich-editor"
                  onInput={handleEditorInput}
                  onKeyDown={handleKeyDown}
                  placeholder="Írja ide a bejelentés tartalmát..."
                  style={{ minHeight: '200px' }}
                  suppressContentEditableWarning={true}
                />
                <div className="editor-info">
                  <small className="character-count">
                    Karakterek: {getTextContent(formData.content).length}
                  </small>
                </div>
              </div>
              <small className="form-help">
                <strong>Formázási tippek:</strong> Jelölje ki a szöveget és kattintson a formázó gombokra. 
                Enter = új sor.
              </small>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingAnnouncement ? 'Frissítés' : 'Közzététel'}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => {
                  setShowEditor(false);
                  setEditingAnnouncement(null);
                  setFormData({ title: '', content: '', type: 'info', pinned: false });
                }}
              >
                Mégse
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="ann-list-section">
        <div className="ann-list-toolbar">
          <h3>Közzétett Bejelentések ({publishedVisibleCount})</h3>
          <div className="ann-list-filters">
            <div className="ann-search-wrap">
              <span className="ann-search-icon" aria-hidden>🔍</span>
              <input
                type="search"
                className="ann-search-input"
                placeholder="Keresés..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
          </div>
            <select
              className="ann-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Összes állapot</option>
              <option value="published">Közzétéve</option>
              <option value="draft">Piszkozat</option>
              <option value="archived">Archivált</option>
            </select>
          </div>
        </div>

        {isLoading && announcements.length === 0 ? (
          <div className="ann-loading">Betöltés...</div>
        ) : filteredAnnouncements.length === 0 ? (
          <div className="ann-empty-state">
            <p>
              {announcements.length === 0
                ? 'Még nincsenek bejelentések. Hozza létre az első bejelentést!'
                : 'Nincs a keresésnek megfelelő bejelentés.'}
            </p>
          </div>
        ) : (
          <div className="ann-cards-list">
            {filteredAnnouncements.map((announcement) => {
              const status = announcement.status || 'published';
              const previewText = getTextContent(announcement.content);
              const authorInitial = (announcement.author || 'A').charAt(0).toUpperCase();
              return (
                <article
                  key={announcement._id}
                  className={`ann-card ann-card-${announcement.type} ${announcement.pinned ? 'is-pinned' : ''} ${getStatusClass(status)}`}
                >
                  <div className="ann-card-top">
                    <div className="ann-card-badges">
                      <span className={`ann-badge ann-badge-type ann-badge-${announcement.type}`}>
                      {getTypeIcon(announcement.type)} {getTypeLabel(announcement.type)}
                    </span>
                      <span className={`ann-badge ann-badge-status ${getStatusClass(status)}`}>
                        ✓ {getStatusLabel(status)}
                      </span>
                      <span className="ann-badge ann-badge-date">
                        📅 {formatAnnouncementDate(announcement.createdAt)}
                    </span>
                  </div>
                    {status === 'published' && aktualitasokPageEnabled && (
                      <span className="ann-badge ann-badge-public">🌐 Nyilvános</span>
                    )}
                </div>

                  {announcement.title && (
                    <h4 className="ann-card-title">{announcement.title}</h4>
                  )}

                  <div className="ann-card-preview">
                    <p>{previewText.length > 280 ? `${previewText.substring(0, 280)}...` : previewText}</p>
                </div>

                  {announcement.pinned && <span className="ann-pinned-tag">📌 Kiemelt</span>}

                  <div className="ann-card-footer">
                    <div className="ann-author">
                      <span className="ann-author-avatar">{authorInitial}</span>
                      <div className="ann-author-meta">
                        <span className="ann-author-name">{announcement.author || 'Admin'}</span>
                        <span className="ann-author-role">{announcement.role || 'admin'}</span>
                  </div>
                    </div>
                    <div className="ann-card-actions">
                    <button 
                        type="button"
                        className="ann-action-btn ann-action-edit"
                      onClick={() => handleEdit(announcement)}
                      disabled={isLoading}
                    >
                      ✏️ Szerkesztés
                    </button>
                    <button 
                        type="button"
                        className="ann-action-btn ann-action-delete"
                      onClick={() => handleDelete(announcement._id)}
                      disabled={isLoading}
                    >
                      🗑️ Törlés
                    </button>
                  </div>
                </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Menu Manager Component
const MenuManager = ({ showToast }) => {
  const [selectedWeek, setSelectedWeek] = useState('current');
  const [menus, setMenus] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  const menuCategories = [
    { id: 'normal', name: 'Normál', icon: '🍽️' },
    { id: 'diabetic', name: 'Diabetikus', icon: '🩺' },
    { id: 'soft', name: 'Pépes', icon: '🥄' },
    { id: 'gentle', name: 'Kímélő', icon: '🌿' },
    { id: 'vital', name: 'Vitál', icon: '💪' },
    { id: 'dairy-free', name: 'Tejmentes', icon: '🥛' },
    { id: 'lactose-free', name: 'Laktózmentes', icon: '🚫' }
  ];

  // Load existing menus
  useEffect(() => {
    loadMenus();
  }, [selectedWeek]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMenus = async () => {
    try {
      const currentYear = new Date().getFullYear();
      const response = await fetch(`${API_BASE_URL}/menus?week=${selectedWeek}&year=${currentYear}`);
      
      if (response.ok) {
        const data = await response.json();
        const menusMap = {};
        data.menus.forEach(menu => {
          menusMap[menu.category] = menu;
        });
        setMenus(menusMap);
      }
    } catch (error) {
      console.error('Failed to load menus:', error);
    }
  };

  const handleFileUpload = async (event, categoryId, week) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      showToast('Kérjük, csak PDF fájlokat töltsön fel!', 'error');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      showToast('A fájl mérete nem lehet nagyobb 10MB-nál!', 'error');
      return;
    }

    setIsUploading(true);
    setUploadProgress(prev => ({ ...prev, [categoryId]: 0 }));

    try {
      const formData = new FormData();
      formData.append('menuPdf', file);
      formData.append('category', categoryId);
      formData.append('week', week);

      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/menus/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setUploadProgress(prev => ({ ...prev, [categoryId]: 100 }));
        showToast(
          `PDF sikeresen feltöltve!\nKategória: ${menuCategories.find(c => c.id === categoryId)?.name}\nHét: ${week === 'current' ? 'Aktuális' : 'Következő'}`
        );
        
        // Reload menus to show the new upload
        await loadMenus();
        
        // Clear progress after a delay
        setTimeout(() => {
          setUploadProgress(prev => ({ ...prev, [categoryId]: 0 }));
        }, 2000);
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showToast(`Hiba a feltöltés során: ${error.message}`, 'error');
      setUploadProgress(prev => ({ ...prev, [categoryId]: 0 }));
    } finally {
      setIsUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleViewMenu = (menu) => {
    if (menu && menu._id) {
      const pdfUrl = `${API_BASE_URL}/menus/pdf/${menu._id}`;
      window.open(pdfUrl, '_blank');
    } else {
      showToast('Nincs elérhető PDF fájl ehhez a kategóriához.', 'error');
    }
  };

  const handleDeleteMenu = async (menu, categoryName) => {
    if (!menu?._id) return;
    const confirmed = window.confirm(
      `Törli a(z) "${categoryName}" kategória ${selectedWeek === 'current' ? 'aktuális' : 'következő'} heti étlap PDF-jét?`
    );
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/menus/${menu._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Törlés sikertelen');
      await loadMenus();
      showToast(`A(z) „${categoryName}” étlap PDF törölve.`);
    } catch (error) {
      console.error('Delete menu error:', error);
      showToast(`Hiba a törlés során: ${error.message}`, 'error');
    }
  };

  return (
    <div className="menu-manager">
      <h2>🍽️ Heti Étlap Kezelése</h2>
      
      <div className="menu-controls">
        <div className="week-selector">
          <label>Hét kiválasztása:</label>
          <select value={selectedWeek} onChange={(e) => setSelectedWeek(e.target.value)}>
            <option value="current">Aktuális hét</option>
            <option value="next">Következő hét</option>
          </select>
        </div>
      </div>

      <div className="menu-categories">
        {menuCategories.map(category => {
          const currentMenu = menus[category.id];
          const progress = uploadProgress[category.id] || 0;
          
          return (
            <div key={category.id} className="category-card">
              <div className="category-header">
                <span className="category-icon">{category.icon}</span>
                <h3>{category.name}</h3>
              </div>
              
              <div className="category-content">
                <div className="current-file">
                  <p><strong>Jelenlegi fájl:</strong></p>
                  {currentMenu ? (
                    <>
                      <p className="file-name">{currentMenu.pdfFile.originalName}</p>
                      <p className="file-info">
                        Feltöltve: {new Date(currentMenu.pdfFile.uploadDate).toLocaleDateString('hu-HU')}
                        <br />
                        Méret: {(currentMenu.pdfFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      <button 
                        className="view-btn"
                        onClick={() => handleViewMenu(currentMenu)}
                      >
                        Megtekintés
                      </button>
                      <button
                        type="button"
                        className="menu-delete-btn"
                        onClick={() => handleDeleteMenu(currentMenu, category.name)}
                        disabled={isUploading}
                      >
                        Törlés
                      </button>
                    </>
                  ) : (
                    <p className="no-file">Nincs feltöltött fájl</p>
                  )}
                </div>
                
                <div className="upload-section">
                  <label className={`upload-btn ${isUploading ? 'uploading' : ''}`}>
                    📄 {currentMenu ? 'PDF Cseréje' : 'Új PDF Feltöltése'}
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => handleFileUpload(e, category.id, selectedWeek)}
                      style={{ display: 'none' }}
                      disabled={isUploading}
                    />
                  </label>
                  
                  {progress > 0 && (
                    <div className="upload-progress">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                      <p>Feltöltés: {progress}%</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MAX_DOCUMENT_MB = 20;

async function documentUploadErrorMessage(response, fallback) {
  if (response.status === 413) {
    return `A fájl túl nagy (max. ${MAX_DOCUMENT_MB} MB). Használjon kisebb PDF-et, vagy kérje a szerver nginx beállításának frissítését.`;
  }
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      const data = await response.json();
      return data.error || fallback;
    } catch {
      return fallback;
    }
  }
  return fallback;
}

// Shared document list section (nav menu or footer legal links)
const DocumentSection = ({
  apiCall,
  placement,
  title,
  description,
  fileInputId,
  showToast
}) => {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newName, setNewName] = useState('');
  const [newFile, setNewFile] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [draggedDocId, setDraggedDocId] = useState(null);
  const [dragOverDocId, setDragOverDocId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [replacingDocId, setReplacingDocId] = useState(null);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const list = await apiCall(`/documents?placement=${placement}`);
      setDocuments(list || []);
    } catch (e) {
      console.error('Failed to load documents:', e);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, [placement]);

  const getDocLink = (doc) => {
    const url = getDocumentUrl(doc);
    return url === '#' ? null : url;
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!newName.trim()) {
      showToast('Adja meg a megjelenítendő nevet.', 'error');
        return;
      }
    if (!newFile) {
      showToast('Válasszon fájlt a feltöltéshez.', 'error');
        return;
      }
      
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('name', newName.trim());
      formData.append('placement', placement);
      formData.append('file', newFile);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (!response.ok) {
        throw new Error(await documentUploadErrorMessage(response, 'Feltöltés sikertelen'));
      }
      setNewName('');
      setNewFile(null);
      const fileEl = document.getElementById(fileInputId);
      if (fileEl) fileEl.value = '';
      await loadDocuments();
      showToast('Dokumentum sikeresen feltöltve!');
    } catch (err) {
      showToast(err.message || 'Hiba a feltöltés során.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveEdit = async (id) => {
    if (!editName.trim()) return;
    try {
      await apiCall(`/documents/${id}`, 'PUT', { name: editName.trim() });
      setEditingId(null);
      setEditName('');
      await loadDocuments();
      showToast('Dokumentum neve frissítve.');
    } catch (err) {
      showToast(err.message || 'Hiba a mentés során.', 'error');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Törli a „${name}” dokumentumot?`)) return;
    try {
      await apiCall(`/documents/${id}`, 'DELETE');
      await loadDocuments();
      showToast('Dokumentum törölve.');
    } catch (err) {
      showToast(err.message || 'Hiba a törlés során.', 'error');
    }
  };

  const handleReplaceFile = async (docId, file) => {
    if (!file) {
      showToast('Válasszon fájlt a cseréhez.', 'error');
        return;
      }
    setReplacingDocId(docId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/documents/${docId}/file`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!response.ok) {
        throw new Error(await documentUploadErrorMessage(response, 'Fájlcsere sikertelen'));
      }
      await loadDocuments();
      showToast('Dokumentum fájlja frissítve.');
    } catch (err) {
      showToast(err.message || 'Hiba a fájl cseréje során.', 'error');
    } finally {
      setReplacingDocId(null);
    }
  };

  const saveDocumentOrder = async (orderedDocs) => {
    setSavingOrder(true);
    try {
      await Promise.all(
        orderedDocs.map((doc, index) =>
          apiCall(`/documents/${doc._id}`, 'PUT', { order: index })
        )
      );
      await loadDocuments();
    } catch (err) {
      showToast(err.message || 'Hiba a sorrend mentése során.', 'error');
      await loadDocuments();
      } finally {
      setSavingOrder(false);
    }
  };

  const reorderDocuments = (sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId || savingOrder) return;
    const sourceIndex = documents.findIndex((doc) => doc._id === sourceId);
    const targetIndex = documents.findIndex((doc) => doc._id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) return;
    const nextDocuments = [...documents];
    const [movedDoc] = nextDocuments.splice(sourceIndex, 1);
    nextDocuments.splice(targetIndex, 0, movedDoc);
    const orderedDocs = nextDocuments.map((doc, index) => ({ ...doc, order: index }));
    setDocuments(orderedDocs);
    saveDocumentOrder(orderedDocs);
  };

  const handleDragStart = (event, id) => {
    setDraggedDocId(id);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', id);
  };

  const handleDragOver = (event, id) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverDocId(id);
  };

  const handleDrop = (event, targetId) => {
    event.preventDefault();
    const sourceId = event.dataTransfer.getData('text/plain') || draggedDocId;
    setDraggedDocId(null);
    setDragOverDocId(null);
    reorderDocuments(sourceId, targetId);
  };

  const handleDragEnd = () => {
    setDraggedDocId(null);
    setDragOverDocId(null);
  };

  const canSubmit = newName.trim() && newFile;

  return (
    <section className={`navdoc-section navdoc-section--${placement}`}>
      <h2>{title}</h2>
      <p className="navdoc-desc">{description}</p>

      <form onSubmit={handleUpload} className="navdoc-upload-form">
        <div className="navdoc-form-row">
                <div className="form-group">
            <label>Megjelenítendő név</label>
                  <input
                    type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={placement === 'footer' ? 'pl. Adatvédelmi Szabályzat' : 'pl. Házirend, Adatlapok'}
              maxLength={200}
                  />
                </div>
                <div className="form-group">
            <label>Fájl (PDF, DOC, DOCX, JPG, PNG – max {MAX_DOCUMENT_MB} MB)</label>
                  <input
              id={fileInputId}
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={(e) => setNewFile(e.target.files?.[0] || null)}
            />
                </div>
                </div>
        <button type="submit" className="navdoc-upload-btn" disabled={uploading || !canSubmit}>
          {uploading ? 'Feltöltés...' : '➕ Dokumentum hozzáadása'}
                </button>
            </form>

      <div className="navdoc-list-section">
        <h3>Dokumentumok ({documents.length})</h3>
        {documents.length > 1 && (
          <p className="navdoc-order-hint">
            Húzza át a sorrend módosításához.
            {savingOrder ? ' Sorrend mentése...' : ''}
          </p>
        )}
        {isLoading ? (
          <p>Betöltés...</p>
        ) : documents.length === 0 ? (
          <p className="navdoc-no-docs">Még nincs dokumentum. A fenti űrlappal adhat hozzá.</p>
        ) : (
          <ul className={`navdoc-list ${savingOrder ? 'navdoc-list--saving' : ''}`}>
            {documents.map((doc) => {
              const docLink = getDocLink(doc);
  return (
                <li
                  key={doc._id}
                  className={`navdoc-item ${draggedDocId === doc._id ? 'navdoc-item--dragging' : ''} ${dragOverDocId === doc._id ? 'navdoc-item--drag-over' : ''}`}
                  draggable={editingId !== doc._id && !savingOrder}
                  onDragStart={(event) => handleDragStart(event, doc._id)}
                  onDragOver={(event) => handleDragOver(event, doc._id)}
                  onDrop={(event) => handleDrop(event, doc._id)}
                  onDragEnd={handleDragEnd}
                >
                  {editingId === doc._id ? (
                    <div className="navdoc-edit-inline">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit(doc._id)}
                      />
                      <button type="button" className="navdoc-save-btn" onClick={() => handleSaveEdit(doc._id)}>Mentés</button>
                      <button type="button" className="navdoc-cancel-btn" onClick={() => { setEditingId(null); setEditName(''); }}>Mégse</button>
        </div>
                  ) : (
                    <>
                      <span className="navdoc-name">
                        <span className="navdoc-drag-handle" aria-hidden="true">⋮⋮</span>
                        <span className="navdoc-name-text">
                          {doc.name}
                          {doc.fileMissing && (
                            <span className="navdoc-missing-badge">
                              Fájl hiányzik ezen a gépen — töltse fel újra, vagy szinkronizálja az éles szerverről
                            </span>
                          )}
                        </span>
                      </span>
                      <div className="navdoc-actions">
                        {docLink && !doc.fileMissing && (
                          <a href={docLink} target="_blank" rel="noopener noreferrer" className="navdoc-view-btn">Megtekintés</a>
                        )}
                        <label className="navdoc-replace-btn">
              <input
                type="file"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf"
                            className="navdoc-replace-input"
                            disabled={replacingDocId === doc._id}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleReplaceFile(doc._id, file);
                              e.target.value = '';
                            }}
                          />
                          {replacingDocId === doc._id ? 'Feltöltés...' : 'Fájl cseréje'}
            </label>
                        <button
                          type="button"
                          className="navdoc-edit-btn"
                          onClick={() => {
                            setEditingId(doc._id);
                            setEditName(doc.name);
                          }}
                        >
                          Szerkesztés
                        </button>
                        <button type="button" className="navdoc-delete-btn" onClick={() => handleDelete(doc._id, doc.name)}>Törlés</button>
                </div>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
            )}
          </div>
    </section>
  );
};

const DocumentsManager = ({ apiCall, showToast }) => (
  <div className="navdoc-manager">
    <DocumentSection
      apiCall={apiCall}
      showToast={showToast}
      placement="nav"
      title="📄 Dokumentumok (menü / dropdown)"
      description="A navigációs „Dokumentumok” menüben megjelenő fájlok. A megadott név jelenik meg a legördülő listában."
      fileInputId="doc-file-input-nav"
    />

    <DocumentSection
      apiCall={apiCall}
      showToast={showToast}
      placement="footer"
      title="⚖️ Lábléc – jogi dokumentumok"
      description="A weboldal láblécében, a „Jogi Információk” alatt megjelenő linkek (pl. ÁSZF, adatvédelmi szabályzat). Itt tölthet fel és szerkeszthet PDF dokumentumokat."
      fileInputId="doc-file-input-footer"
    />
    </div>
  );


export default AdminPanel;
